import { useMemo, useState } from "react";
import {
  CheckCircle2,
  FileOutput,
  Printer,
  RefreshCw,
  Send,
  ShieldAlert,
} from "lucide-react";
import {
  compileApplicableDeliverables,
  deliverableHtml,
} from "../lib/deliverableCompiler";
import type {
  ApprovalRecord,
  ControlledDeliverable,
  DeliverableStatus,
  ProjectWorkspace,
} from "../types/domain";

interface DeliverablesViewProps {
  workspace: ProjectWorkspace;
  onSave: (deliverable: ControlledDeliverable) => void;
  onRequestApproval: (approval: ApprovalRecord) => void;
}

const download = (name: string, content: string) => {
  const url = URL.createObjectURL(new Blob([content], { type: "text/html;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
};

export function DeliverablesView({
  workspace,
  onSave,
  onRequestApproval,
}: DeliverablesViewProps) {
  const [selectedId, setSelectedId] = useState("");
  const compiled = useMemo(() => compileApplicableDeliverables(workspace), [workspace]);
  const selected =
    compiled.find((item) => item.id === selectedId) ??
    compiled.find((item) => item.type === selectedId) ??
    compiled[0];
  const missing = selected?.sections.filter((item) => item.status === "missing").length ?? 0;
  const incomplete =
    selected?.sections.filter((item) => item.status === "incomplete").length ?? 0;

  const saveWith = (changes: Partial<ControlledDeliverable>) => {
    if (!selected) return;
    onSave({ ...selected, ...changes });
  };

  const requestApproval = () => {
    if (!selected) return;
    const approval: ApprovalRecord = {
      id: crypto.randomUUID(),
      entityType: "deliverable",
      entityId: selected.id,
      step: `${selected.title} issue approval`,
      status: "requested",
      requester: selected.owner,
      reviewer: selected.reviewer,
      requestedAt: new Date().toISOString(),
      decidedAt: "",
      comment: "",
    };
    onSave({ ...selected, status: "in-review", approvalId: approval.id });
    onRequestApproval(approval);
  };

  return (
    <>
      <section className="module-intro">
        <div>
          <span className="feature-kicker">
            <FileOutput size={17} /> CDM deliverable compiler
          </span>
          <h2>Compile controlled project documents from the assurance graph.</h2>
          <p>
            PCI, design-risk assessment, principal appointments, JKKP 12 working
            record, construction phase plan and Safety and Health File are assembled
            from live project records. Missing evidence stays explicit.
          </p>
        </div>
        <div className="module-stat">
          <strong>{compiled.length}</strong>
          <span>applicable outputs</span>
        </div>
      </section>

      <div className="deliverable-layout">
        <aside className="deliverable-list" aria-label="Applicable deliverables">
          {compiled.map((item) => {
            const missingCount = item.sections.filter(
              (section) => section.status === "missing",
            ).length;
            const saved = workspace.deliverables.find(
              (existing) => existing.type === item.type,
            );
            return (
              <button
                key={item.type}
                className={selected?.type === item.type ? "active" : ""}
                onClick={() => setSelectedId(item.type)}
              >
                <span>
                  <strong>{item.title}</strong>
                  <small>{saved?.status ?? item.status} · Rev {saved?.revision ?? item.revision}</small>
                </span>
                <em className={missingCount ? "status-warning" : "status-success"}>
                  {missingCount ? `${missingCount} gaps` : "ready"}
                </em>
              </button>
            );
          })}
        </aside>

        {selected && (
          <section className="deliverable-workbench">
            <header>
              <div>
                <span className="eyebrow">{selected.policyPackVersion}</span>
                <h2>{selected.title}</h2>
                <p>{selected.reference} · Revision {selected.revision}</p>
              </div>
              <div className="deliverable-readiness">
                <strong>{selected.sections.length - missing - incomplete}/{selected.sections.length}</strong>
                <span>sections ready</span>
              </div>
            </header>

            <div className="form-grid compact-grid">
              <label className="field">
                <span>Reference</span>
                <input
                  value={selected.reference}
                  onChange={(event) => saveWith({ reference: event.target.value })}
                />
              </label>
              <label className="field">
                <span>Revision</span>
                <input
                  value={selected.revision}
                  onChange={(event) => saveWith({ revision: event.target.value })}
                />
              </label>
              <label className="field">
                <span>Owner</span>
                <input
                  value={selected.owner}
                  onChange={(event) => saveWith({ owner: event.target.value })}
                />
              </label>
              <label className="field">
                <span>Reviewer</span>
                <input
                  value={selected.reviewer}
                  onChange={(event) => saveWith({ reviewer: event.target.value })}
                />
              </label>
              <label className="field">
                <span>Status</span>
                <select
                  value={selected.status}
                  onChange={(event) =>
                    saveWith({ status: event.target.value as DeliverableStatus })
                  }
                >
                  <option value="not-started">Not started</option>
                  <option value="draft">Draft</option>
                  <option value="in-review">In review</option>
                  <option value="approved">Approved</option>
                  <option value="issued">Issued</option>
                </select>
              </label>
            </div>

            <div className="deliverable-actions">
              <button className="button button-secondary" onClick={() => onSave(selected)}>
                <RefreshCw size={16} /> Refresh from project
              </button>
              <button
                className="button button-secondary"
                onClick={() =>
                  download(
                    `${selected.reference || selected.type}-${selected.revision}.html`,
                    deliverableHtml(selected, workspace),
                  )
                }
              >
                <Printer size={16} /> Export printable file
              </button>
              <button
                className="button button-primary"
                disabled={missing > 0 || !selected.owner || !selected.reviewer}
                onClick={requestApproval}
              >
                <Send size={16} /> Request issue approval
              </button>
            </div>

            {(missing > 0 || incomplete > 0) && (
              <div className="evidence-guidance">
                <ShieldAlert size={20} />
                <div>
                  <strong>Issue gate is not satisfied</strong>
                  <p>
                    {missing} missing and {incomplete} incomplete section(s) must be
                    resolved or explicitly accepted before issue.
                  </p>
                </div>
              </div>
            )}

            <div className="deliverable-sections">
              {selected.sections.map((section, index) => (
                <article key={section.id} className={`deliverable-section section-${section.status}`}>
                  <header>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <div>
                      <h3>{section.title}</h3>
                      <p>{section.requirement}</p>
                    </div>
                    <em>
                      {section.status === "ready" && <CheckCircle2 size={15} />}
                      {section.status}
                    </em>
                  </header>
                  <pre>{section.content}</pre>
                  <small>
                    {section.sourceEntityIds.length} source record(s) ·{" "}
                    {section.evidenceIds.length} evidence link(s)
                  </small>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
